import { DndContext } from '@dnd-kit/core';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { usePriorityTodayReorder } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskCard } from 'shared/components/connected/task-card';
import { OverlayComponentWrapper } from 'shared/components/ui/overlay-component-wrapper';
import { SortableOverlay } from 'shared/components/ui/sortable-overlay';
import { TaskCardTaskSection } from 'shared/components/ui/task-card-task-section';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useActiveTaskId } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useDroppableSectionControls } from 'shared/hooks/use-droppable-section-controls';
import { useMouseOutside } from 'shared/hooks/use-mouse-outside';
import { useSectionCollisionDetection } from 'shared/hooks/use-section-collision-detection';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { useUser } from 'shared/hooks/use-user';
import { DueDisplayOptions, TaskPriorityOptions } from 'shared/types/task-base';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { noCollisionDetection } from 'shared/utils/no-collision-detection';

import { useFullCalendarDraggableTasks } from '../../hooks/use-full-calendar-draggable-tasks';
import { usePriorityTaskSections } from '../../hooks/use-priority-task-sections';
import { SectionHeaderToday } from '../section-header-today';
import * as Styled from './priority-grouping-tasks-list.style';

const displayDue = [DueDisplayOptions.Past, DueDisplayOptions.Future];

export const PriorityGroupingTasksList: React.FC = () => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const containerRef = useRef<HTMLDivElement>(null);

  const openPremiumDialog = useOpenPremiumDialog();

  const { taskCardTasks, sections: initialSections } =
    usePriorityTaskSections();
  const onDragEnd = usePriorityTodayReorder(initialSections);

  const {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    activeId,
    sections,
    setSections,
  } = useDroppableSectionControls({ initialSections, onDragEnd });
  const sensors = useSortableSensors();
  const collisionStrategy = useSectionCollisionDetection(activeId, sections);

  const selectedTask = useActiveTaskId();

  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const activeItem = useMemo(
    () =>
      activeId ? taskCardTasks.find(({ id }) => id === activeId) : undefined,
    [activeId, taskCardTasks],
  );

  const tasks = useMemo(
    () => taskCardTasks.map(({ raw }) => raw.task),
    [taskCardTasks],
  );
  useFullCalendarDraggableTasks(tasks, containerRef);

  const onMouseLeave = useCallback(
    () => setSections(initialSections),
    [initialSections, setSections],
  );

  const isOutsideList = useMouseOutside(containerRef, {
    enabled: !!activeId,
    onMouseLeave,
  });

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={
        isOutsideList ? noCollisionDetection : collisionStrategy
      }
    >
      <Styled.Container ref={containerRef}>
        {sections.map(({ id, items }) => (
          <Styled.Section key={id}>
            <TaskCardTaskSection
              droppableId={id}
              priority={id as TaskPriorityOptions}
              tagIds={user?.settings?.todayFilterByTags}
              timeFormat={getTimeFormat(user)}
              goals={goals}
              lifeAreas={lifeAreas}
              tasks={items}
              selectedTask={selectedTask}
              date={today}
              displayDue={displayDue}
              customHeader={
                <SectionHeaderToday
                  title={t(`general.sections.priority.${id}.title`)}
                />
              }
              onPremium={openPremiumDialog}
              canCollapse
              canCreateTask
              canReorder
            />
          </Styled.Section>
        ))}
      </Styled.Container>

      <SortableOverlay>
        {!!activeItem && (
          <OverlayComponentWrapper isOutside={isOutsideList}>
            <TaskCard task={activeItem.raw.task} displayDue={displayDue} />
          </OverlayComponentWrapper>
        )}
      </SortableOverlay>
    </DndContext>
  );
};
